/* -----------------------------------------------------------------------
 * <copyright company="Microsoft Corporation">
 *   Copyright (c) Microsoft Corporation.  All rights reserved.
 * </copyright>
 * ----------------------------------------------------------------------- */
import * as React from "react";
import { Icon } from "@fluentui/react/lib/Icon";
import {
  ITimelineProps,
  ITimelineStyles,
  ITimelineStylesProps,
} from "./Timeline.types";
import { classNamesFunction, Spinner, SpinnerSize } from "@fluentui/react";
import { useState } from "react";
import moment from "moment";
import { applicationApi } from "../../services/application-apis/application-apis";
import { spinnerStyle } from "./Timeline.Styles";
import { base64ToArrayBuffer } from "../util";
import { logTelemetry } from "../../services/telemetry/telemetryWrapper";
import {
  ApplicationStatus,
  TelemetryLogs,
  TelemetrySubFeature,
} from "../constants";
import { ActionType, EventName } from "@microsoftit/telemetry-extensions-npm";

const getClassNames = classNamesFunction<
  ITimelineStylesProps,
  ITimelineStyles
>();

export const Timeline: React.FC<ITimelineProps> = (props: ITimelineProps) => {
  const [isDownloadProgress, setIsDownloadProgress] = useState<boolean[]>([]);
  const { statusText, history, styles, theme, status } = props;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const classNameFromStyles = getClassNames(styles, { theme: theme! });

  //Fetching file from server
  const _fetchReferenceLetter = async (referenceId: number, key: number) => {
    try {
      isDownloadProgress[key] = true;
      setIsDownloadProgress([...isDownloadProgress]);
      const response = await applicationApi.getReferenceHistoryFile(
        referenceId
      );
      if (response.status === 200) {
        const blob = new Blob(
          [
            base64ToArrayBuffer(
              response.data.operationResult.result.fileContent
            ),
          ],
          {
            type: "application/octet-stream",
          }
        );
        const anchor = document.createElement("a");
        anchor.download =
          response.data.operationResult?.result?.fileName ?? "reference_letter";
        anchor.href = (window.webkitURL || window.URL).createObjectURL(blob);
        anchor.dataset.downloadurl = [
          "text/plain",
          anchor.download,
          anchor.href,
        ].join(":");
        anchor.click();
        logTelemetry(
          TelemetryLogs.PageViews,
          TelemetrySubFeature.LetterDownload,
          ActionType.User,
          "File downloaded for referenceId: " + referenceId,
          EventName.LinkClicked
        );
        isDownloadProgress[key] = false;
        setIsDownloadProgress([...isDownloadProgress]);
      } else {
        isDownloadProgress[key] = false;
        setIsDownloadProgress([...isDownloadProgress]);
      }
    } catch (e) {
      isDownloadProgress[key] = false;
      setIsDownloadProgress([...isDownloadProgress]);
    } finally {
      isDownloadProgress[key] = false;
      setIsDownloadProgress([...isDownloadProgress]);
    }
  };

  return (
    <div className={classNameFromStyles.root}>
      <h3>
        {status === ApplicationStatus.PENDING && (
          <p className={classNameFromStyles.pendingText}>
            It may take a few minutes for the latest status and uploaded file to
            reflect here.
          </p>
        )}
      </h3>
      {history && history.length > 0 ? (
        <div className={classNameFromStyles.divContainer}>
          <ul className={classNameFromStyles.ulContainer}>
            {history.map((historyItems, keyIndex) => {
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              const localDate = moment(new Date(historyItems.statusDate!))
                .local()
                .format("DD MMM YYYY");
              return (
                <li key={keyIndex} className={classNameFromStyles.liContainer}>
                  {historyItems.status === "Complete" ? (
                    <div className={classNameFromStyles.liContent}>
                      <Icon
                        iconName="SkypeCircleCheck"
                        className={classNameFromStyles.greenIcon}
                      />
                      <div className={classNameFromStyles.textContainer}>
                        <span
                          data-testid={"data-success-list"}
                          className={classNameFromStyles.statusText}
                          tabIndex={0}
                          aria-labelledby={
                            statusText?.successText + " " + localDate
                          }
                        >
                          {statusText?.successText} {localDate}
                        </span>
                        {isDownloadProgress[keyIndex] === false ||
                        isDownloadProgress[keyIndex] === undefined ? (
                          <span
                            className={classNameFromStyles.downloadText}
                            aria-labelledby={
                              "Click here to " + statusText?.downloadText
                            }
                            tabIndex={0}
                            onClick={() =>
                              _fetchReferenceLetter(
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                historyItems.referenceId!,
                                keyIndex
                              )
                            }
                            onKeyDown={() =>
                              _fetchReferenceLetter(
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                historyItems.referenceId!,
                                keyIndex
                              )
                            }
                          >
                            <Icon iconName="TextDocument"></Icon>
                            <span
                              data-testid={"data-download-text"}
                              className={classNameFromStyles.downloadSubText}
                            >
                              {statusText?.downloadText}
                            </span>
                            <Icon
                              iconName="Download"
                              className={classNameFromStyles.downloadIcon}
                            />
                          </span>
                        ) : (
                          <span
                            className={classNameFromStyles.spinnerContainer}
                          >
                            <Spinner
                              styles={spinnerStyle}
                              size={SpinnerSize.xSmall}
                              label="Downloading file, please wait"
                            />
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className={classNameFromStyles.liContent}>
                      <Icon
                        iconName="CircleFill"
                        className={classNameFromStyles.redIcon}
                      />
                      <span
                        data-testid={"data-declined-list"}
                        className={classNameFromStyles.declineStatusText}
                        tabIndex={0}
                        aria-labelledby={
                          statusText?.declinedText + " " + localDate
                        }
                      >
                        {statusText?.declinedText} {localDate}
                      </span>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <p
          data-testid={"data-empty-result"}
          className={classNameFromStyles.paraGraphText}
          tabIndex={0}
          aria-labelledby={statusText?.noResultText}
        >
          {statusText?.noResultText}
        </p>
      )}
    </div>
  );
};
